import { Card, Col, Divider, Row, Typography } from "antd";
import type { ApiProdutor } from "pages/produtores/types/ApiProdutor";
import { Fragment } from "react";
import { formatDocument } from "std/format";
import { styled } from "styled-components";

const { Text } = Typography;

const TextTitleRight = styled(Text)`
  flex: 0 0 128px;
  font-weight: bold;
  text-align: right;
`;

const TextTitle = styled(Text)`
  flex: 0 0 100px;
  font-weight: bold;
  text-align: right;
`;

const TextInfo = styled(Text)`
  flex: 1 1 calc(100% - 104px);
`;

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export function TableCard({ produtor }: { produtor: ApiProdutor }) {
	const getTipoEmail = (email) => {
		const tipo: string[] = [];

		if (email?.ema_geral) {
			tipo.push("Geral");
		}

		if (email?.ema_comercial) {
			tipo.push("Comercial");
		}

		if (email?.ema_financeiro) {
			tipo.push("Financeiro");
		}

		if (email?.ema_documentos_fiscais) {
			tipo.push("Fiscal");
		}

		return tipo.join(" - ");
	};

	const getTipoConta = (tipo) => {
		if (tipo === 1) {
			return "Conta corrente";
		}

		if (tipo === 2) {
			return "Conta poupança";
		}

		return "";
	};

	return (
		<Card>
			<Row>
				<StyledCol flex="1 1 50%">
					<TextTitle>Código:</TextTitle>
					<TextInfo>{produtor?.for_codigoindividual || ""}</TextInfo>
				</StyledCol>

				<StyledCol flex="0 1 300px">
					<TextTitleRight>CNPJ/CPF:</TextTitleRight>
					<TextInfo>{formatDocument(produtor?.pessoa?.pes_cnpj_cpf || "")}</TextInfo>
				</StyledCol>

				<StyledCol flex="1 1 50%">
					<TextTitle>RG/IE:</TextTitle>
					<TextInfo>{produtor?.pessoa?.pes_ie_rg}</TextInfo>
				</StyledCol>

				<StyledCol flex="0 1 300px">
					<TextTitleRight>{produtor?.pessoa?.pes_tipo_pessoa === "J" ? "Data de Cadastro" : "Data de Nascimento"}:</TextTitleRight>
					<TextInfo>{produtor?.pessoa?.pes_data_nascimento_abertura}</TextInfo>
				</StyledCol>
			</Row>

			<Row>
				<StyledCol flex="1 1 50%">
					<TextTitle>{produtor?.pessoa?.pes_tipo_pessoa === "J" ? "Razão Social" : "Nome"}:</TextTitle>
					<TextInfo>{produtor?.pessoa?.pes_nome_razaosocial || ""}</TextInfo>
				</StyledCol>

				<StyledCol flex="0 1 300px">
					<TextTitleRight>Média Litragem:</TextTitleRight>
					<TextInfo>{produtor?.for_lab || ""}</TextInfo>
				</StyledCol>
			</Row>

			<Row>
				{produtor?.pessoa?.pes_tipo_pessoa === "J" && (
					<>
						<StyledCol flex="1 1 50%">
							<TextTitle>Nome Fantasia:</TextTitle>
							<TextInfo>{produtor?.pessoa?.pes_nome_fantasia || ""}</TextInfo>
						</StyledCol>

						<StyledCol flex="0 1 300px">
							<TextTitleRight>NIRF:</TextTitleRight>
							<TextInfo>{produtor?.for_nirf || ""}</TextInfo>
						</StyledCol>

						<StyledCol flex="1 1 50%">
							<TextTitle>NRP:</TextTitle>
							<TextInfo>{produtor?.for_nrp || ""}</TextInfo>
						</StyledCol>
					</>
				)}

				{produtor?.pessoa?.pes_tipo_pessoa !== "J" && (
					<>
						<StyledCol flex="1 1 50%">
							<TextTitle>NIRF:</TextTitle>
							<TextInfo>{produtor?.for_nirf || ""}</TextInfo>
						</StyledCol>

						<StyledCol flex="0 1 300px">
							<TextTitleRight>NRP:</TextTitleRight>
							<TextInfo>{produtor?.for_nrp || ""}</TextInfo>
						</StyledCol>
					</>
				)}
			</Row>

			<Divider />

			<Row>
				<StyledCol xs={24}>
					<TextTitle>Responsável:</TextTitle>
					<TextInfo>{produtor?.pessoa?.pes_responsavel || ""}</TextInfo>
				</StyledCol>

				<StyledCol flex="1 1 50%">
					<TextTitle>Banco:</TextTitle>
					<TextInfo>{produtor?.for_banco || ""}</TextInfo>
				</StyledCol>

				<StyledCol flex="0 1 300px">
					<TextTitleRight>Tipo da Conta:</TextTitleRight>
					<TextInfo>{getTipoConta(produtor?.for_conta_tipo || "")}</TextInfo>
				</StyledCol>

				<StyledCol flex="1 1 50%">
					<TextTitle>Agência:</TextTitle>
					<TextInfo>
						{produtor?.for_agencia || ""}
						{produtor?.for_agencia_dv ? `-${produtor?.for_agencia_dv}` : ""}
					</TextInfo>
				</StyledCol>

				<StyledCol flex="0 1 300px">
					<TextTitleRight>Conta:</TextTitleRight>
					<TextInfo>
						{produtor?.for_conta || ""}
						{produtor?.for_conta_dv ? `-${produtor?.for_conta_dv}` : ""}
					</TextInfo>
				</StyledCol>
			</Row>

			<Divider />

			{(produtor?.pessoa?.endereco || []).map((endereco) => {
				return (
					<Fragment key={endereco?.end_idpk}>
						<Row>
							<StyledCol xs={24}>
								<TextTitle>Endereço:</TextTitle>
								<TextInfo>
									{endereco?.end_logradouro || ""} {endereco?.end_numero ? "N.:" : ""} {endereco?.end_numero || ""}
								</TextInfo>
							</StyledCol>
						</Row>

						<Row>
							<StyledCol flex="1 1 50%">
								<TextTitle>Bairro:</TextTitle>
								<TextInfo>{endereco?.end_bairro || ""}</TextInfo>
							</StyledCol>

							<StyledCol flex="0 1 300px">
								<TextTitleRight>CEP:</TextTitleRight>
								<TextInfo>{endereco?.end_cep || ""}</TextInfo>
							</StyledCol>
						</Row>

						<Row>
							<StyledCol flex="1 1 50%">
								<TextTitle>Cidade:</TextTitle>
								<TextInfo>
									{endereco?.cidade?.cid_idpk || ""} - {endereco?.cidade?.cid_cidade || ""}
								</TextInfo>
							</StyledCol>

							<StyledCol flex="0 1 300px">
								<TextTitleRight>UF:</TextTitleRight>
								<TextInfo>{endereco?.cidade?.cid_uf || ""}</TextInfo>
							</StyledCol>
						</Row>

						<Row>
							<StyledCol xs={24}>
								<TextTitle>Complemento:</TextTitle>
								<TextInfo>{endereco?.end_complemento || ""}</TextInfo>
							</StyledCol>
						</Row>
					</Fragment>
				);
			})}

			<Divider />

			{produtor?.pessoa?.telefone && (
				<Row>
					{(produtor?.pessoa?.telefone || []).map((telefone) => {
						return (
							<Fragment key={telefone?.tel_idpk}>
								<StyledCol xs={6}>
									<TextTitle>{telefone?.tel_descricao || "Telefone"}:</TextTitle>
									<TextInfo>{telefone?.tel_telefone || ""}</TextInfo>
								</StyledCol>
							</Fragment>
						);
					})}
				</Row>
			)}

			<Divider />

			{(produtor?.pessoa?.email || []).map((email) => {
				return (
					<Fragment key={email?.ema_idpk}>
						<Row>
							<StyledCol xs={12}>
								<TextTitle>
									E-mail{" "}
									{email?.ema_geral === 1 ||
									email?.ema_comercial === 1 ||
									email?.ema_financeiro === 1 ||
									email?.ema_documentos_fiscais === 1
										? `(${getTipoEmail(email)})`
										: ""}
									:
								</TextTitle>
								<TextInfo>{email?.ema_email || ""}</TextInfo>
							</StyledCol>
						</Row>
					</Fragment>
				);
			})}
		</Card>
	);
}
